import React from 'react';
import {
  useLocation,
  useNavigate,
  useResolvedPath,
  type To,
} from 'react-router-dom';

import { performCanonicalScrollContinuation } from './canonicalReplace';
import { useOptionalNavigationHistory } from './NavigationHistoryProvider';

/**
 * Replaces a semantic legacy alias while preserving a POP transition's scroll
 * ownership. Authentication and authorization redirects should keep using an
 * ordinary replacement because they are not the same logical page.
 */
export function CanonicalReplaceRedirect({ to }: { to: To }) {
  const location = useLocation();
  const navigate = useNavigate();
  const history = useOptionalNavigationHistory();
  const resolved = useResolvedPath(to);
  const attemptRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const target = {
      pathname: resolved.pathname,
      search: resolved.search,
      hash: resolved.hash,
    };
    if (target.pathname === location.pathname
      && target.search === location.search
      && target.hash === location.hash) return;
    const attempt = JSON.stringify([
      location.key,
      target.pathname,
      target.search,
      target.hash,
    ]);
    if (attemptRef.current === attempt) return;
    attemptRef.current = attempt;
    const replace = () => navigate(target, { replace: true, state: location.state });
    if (!history) {
      void replace();
      return;
    }
    performCanonicalScrollContinuation({
      scopeId: history.scopeId,
      fromLocationKey: location.key,
      ...target,
    }, replace);
  }, [
    history,
    location.hash,
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigate,
    resolved.hash,
    resolved.pathname,
    resolved.search,
  ]);

  return null;
}
